import React, { useState, useEffect } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import { Container, DatePicker, PromptBar, Radio } from 'components/UIElements'
import { DATE_FORMAT_MAP, DEFAULT_VISIT_START_HOUR, DEFAULT_VISIT_START_MINUTE } from 'utils/constants'
import STRINGS from 'utils/strings'
import Subheader from '../../../components/Subheader'
import ParticipantVisitScheduleTable from './ParticipantVisitScheduleTable'
import '../stylesheets/participantVisit.scss'
import { getDayDifference, getVisitDeviationMap, getNewMetadata } from '../../VisitPage/utils/visitDayUtils'
import {
  getIsPastFromRawInput,
  getParsedMomentFromRawInput,
  hasCompleteAdhocDateInput,
  isPastVisitDate,
} from '../utils/ptpAdhocVisitValidation'

function ParticipantVisitDate(props) {
  const {
    closeModal,
    dayChanged,
    disabled,
    disableSubsequentVisitAdjustment,
    errors,
    fetchParticipantVisits,
    isUpdate,
    openModal,
    participantVisit,
    participantVisitsArray,
    pushSubsequent,
    setDaysBeforeVisitPassed,
    setSpecificDateInvalid,
    setPushSubsequent,
    ssvpID,
    studyID,
    updateAdhocSchedule,
    updatePtpVisitMetadata,
  } = props
  const { specificDate: specificDateErr } = errors
  const { adhocVisitSchedule, checkedPtp, visit_metadata = {}, isAdhoc } = participantVisit
  const isPtpChecked = Object.keys(checkedPtp).length > 0
  const [originalDate, setOriginalDate] = useState(null)
  const [scheduledDate, setScheduledDate] = useState(null)

  const [startVisitDateError, setStartVisitDateError] = useState(false)
  const [visitDays, setVisitDays] = useState(1)
  const [visitDate, setVisitDate] = useState('')
  const [dayDiff, setDayDiff] = useState(0)

  const setPastDateError = isPast => {
    setStartVisitDateError(isPast)
    setSpecificDateInvalid(isPast)
  }

  const resetDaysBefore = () => {
    setDaysBeforeVisitPassed(false)
    setVisitDays(1)
    setVisitDate('')
  }

  useEffect(() => {
    return () => {
      updateAdhocSchedule({
        key: 'specificDate',
        value: '',
      })
    }
  }, [])

  useEffect(() => {
    const { original_visit_datetime } = visit_metadata
    if (!originalDate) {
      if (original_visit_datetime) {
        setOriginalDate(moment(original_visit_datetime))
      } else if (adhocVisitSchedule['specificDate'].value) {
        setOriginalDate(moment(adhocVisitSchedule['specificDate'].value))
      }
    }
    if (!scheduledDate && adhocVisitSchedule['specificDate'].value) {
      setScheduledDate(moment(adhocVisitSchedule['specificDate'].value))
    }
    const proposedMoment = moment(adhocVisitSchedule?.specificDate?.value, DATE_FORMAT_MAP.datePickerWithFullTime)
    const dayDifference = getDayDifference(originalDate, proposedMoment)
    setDayDiff(dayDifference)
    const newMetadata = getNewMetadata({ participantVisit, dayDifference })
    updatePtpVisitMetadata(newMetadata)
  }, [adhocVisitSchedule.specificDate?.value, originalDate])

  useEffect(() => {
    resetDaysBefore()
  }, [checkedPtp])

  useEffect(() => {
    const { isSelected } = adhocVisitSchedule.daysBeforeVisit
    if (isSelected) {
      if (visitDays && visitDate !== '') {
        const substractDays = moment(visitDate)
          .subtract(visitDays, 'days')
          .format(DATE_FORMAT_MAP.mainWithFullTime)
        updateAdhocSchedule({
          key: 'daysBeforeVisit',
          value: substractDays,
        })
        if (moment().isAfter(substractDays, 'day')) setDaysBeforeVisitPassed(true)
        else setDaysBeforeVisitPassed(false)
      }
    } else {
      resetDaysBefore()
    }
  }, [adhocVisitSchedule, visitDays, visitDate])

  const persistVisitDate = date => {
    if (date) {
      date.set({ hour: DEFAULT_VISIT_START_HOUR, minute: DEFAULT_VISIT_START_MINUTE })
      updateAdhocSchedule({
        key: 'specificDate',
        value: date.format(DATE_FORMAT_MAP.datePickerWithFullTime),
      })
    } else {
      updateAdhocSchedule({ key: 'specificDate', value: date })
    }
  }

  const handleVisitDateBlur = e => {
    const raw = e?.target?.value ?? ''
    if (!raw.trim()) {
      setPastDateError(false)
      persistVisitDate(null)
      return
    }
    if (!hasCompleteAdhocDateInput(raw)) return

    const date = getParsedMomentFromRawInput(raw)
    if (!date) return

    date.set({ hour: DEFAULT_VISIT_START_HOUR, minute: DEFAULT_VISIT_START_MINUTE })
    setPastDateError(isPastVisitDate(date))
    persistVisitDate(date)
  }

  const onDayChange = (date, options) => {
    if (!disableSubsequentVisitAdjustment) setPushSubsequent(null)
    const inputValue = options?.input?.value ?? ''

    if (!date) {
      setPastDateError(getIsPastFromRawInput(inputValue))
      setPushSubsequent(false)
      return
    }
    if (!hasCompleteAdhocDateInput(inputValue)) {
      setPushSubsequent(false)
      return
    }

    setPastDateError(isPastVisitDate(date))
    persistVisitDate(date)
    setPushSubsequent(false)
  }

  const [currentMetadata, setCurrentMetadata] = useState(null)

  useEffect(() => {
    return () => {
      setCurrentMetadata(null)
    }
  }, [])

  useEffect(() => {
    if (!currentMetadata && !!visit_metadata?.display_name) setCurrentMetadata(visit_metadata)
  }, [participantVisit])

  const cancelChanges = () => {
    updateAdhocSchedule({
      key: 'specificDate',
      value: scheduledDate.format(DATE_FORMAT_MAP.datePickerWithFullTime),
    })
    updatePtpVisitMetadata(currentMetadata)
    setCurrentMetadata(null)
    setPushSubsequent(null)
    setPastDateError(false)
    closeModal()
  }

  const confirmChanges = () => {
    const { value } = adhocVisitSchedule.specificDate
    if (value) {
      const parsed = moment(value, DATE_FORMAT_MAP.datePickerWithFullTime, true)
      setPastDateError(parsed.isValid() && parsed.isBefore(moment(), 'day'))
    }
    closeModal()
  }

  const renderTable = (deviationsOnly = false) => {
    return (
      <ParticipantVisitScheduleTable
        {...props}
        showDeviationsOnly={deviationsOnly}
        dayChanged={dayChanged}
        originalDate={originalDate}
        scheduledDate={scheduledDate}
        dayDiff={dayDiff}
      />
    )
  }

  const confirmationModalContent = () => {
    return (
      <>
        <div className='flexed-header start-justified'>
          <i className='fa-solid fa-triangle-exclamation' />
          <span className='label-small no-margin'>{STRINGS.visitScheduleSelectedDayTolerance}</span>
        </div>
        <p>{STRINGS.visitScheduleProtocolDeviation}</p>
        {renderTable(true)}
      </>
    )
  }

  const visitDeviationMap = getVisitDeviationMap(participantVisitsArray)

  useEffect(() => {
    const numDeviations = Object.values(visitDeviationMap).length
    const hasDeviations = !!numDeviations
    if (!hasDeviations) {
      closeModal()
    } else if (hasDeviations && dayChanged) {
      openModal({
        content: confirmationModalContent(),
        onConfirm: confirmChanges,
        onCancel: cancelChanges,
        className: 'visit-deviation-warning',
        closeOnBackgroundClick: false,
        hideClose: true,
      })
    }
  }, [JSON.stringify(visitDeviationMap)])

  const [lastPtpId, setLastPtpId] = useState(null)

  useEffect(() => {
    const checkedPtpKeyArr = Object.keys(checkedPtp)
    if (checkedPtpKeyArr.length > 0 && lastPtpId !== checkedPtpKeyArr[0]) {
      setLastPtpId(checkedPtpKeyArr[0])
      fetchParticipantVisits(studyID, checkedPtpKeyArr[0], isAdhoc, isUpdate, ssvpID)
    }
  }, [checkedPtp, lastPtpId])

  return (
    isPtpChecked && (
      <Container>
        <div className='adhoc-visit-date'>
          <Subheader subheaderText={STRINGS.visitScheduleWhenShould} />
          <div className='flexed start-justified visit-date-settings'>
            <div className='flex-child'>
              <div>
                <p className='label-small'>{STRINGS.visitScheduleCurrentlyScheduledDate}</p>
                <DatePicker disabled date={scheduledDate} />
              </div>
            </div>
            <div className='flex-child'>
              <div className='new-date'>
                <p className='label-small'>{STRINGS.visitScheduleChangeVisitDate}</p>
                <DatePicker
                  disabled={disabled}
                  disabledDays={{ before: new Date() }}
                  deferParseUntilFullYear
                  preserveInputWhileFocused
                  hasError={startVisitDateError || (specificDateErr && !adhocVisitSchedule.specificDate.value)}
                  errorText={
                    startVisitDateError
                      ? STRINGS.participantValidationVisitDatePast
                      : specificDateErr && !adhocVisitSchedule.specificDate.value
                        ? specificDateErr
                        : undefined
                  }
                  date={
                    adhocVisitSchedule.specificDate.value
                      ? moment(adhocVisitSchedule.specificDate.value, DATE_FORMAT_MAP.datePickerWithFullTime)
                      : adhocVisitSchedule.specificDate.value
                  }
                  onChangeRaw={event => {
                    const raw = event?.target?.value ?? ''
                    if (!raw.trim()) {
                      setPastDateError(false)
                      return
                    }
                    setPastDateError(getIsPastFromRawInput(raw))
                  }}
                  onInputBlur={handleVisitDateBlur}
                  onDayChange={onDayChange}
                />
              </div>
            </div>
            {!disableSubsequentVisitAdjustment && (
              <div className='flex-child visit-push-out'>
                <div>
                  <p className='label-small'>{STRINGS.visitSchedulePushOut}</p>
                  <div className='subsequent-choices flexed start-justified'>
                    <Radio
                      id='push-subsequent-yes'
                      disabled={disabled || disableSubsequentVisitAdjustment}
                      content={STRINGS.yes}
                      selected={pushSubsequent}
                      onClick={() => {
                        setPushSubsequent(true)
                      }}
                    />
                    <Radio
                      id='push-subsequent-no'
                      disabled={disabled || disableSubsequentVisitAdjustment}
                      content={STRINGS.no}
                      selected={pushSubsequent === false}
                      onClick={() => {
                        setPushSubsequent(false)
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          {renderTable()}
        </div>
      </Container>
    )
  )
}

ParticipantVisitDate.propTypes = {
  closeModal: PropTypes.func,
  dayChanged: PropTypes.bool,
  disabled: PropTypes.bool,
  disableSubsequentVisitAdjustment: PropTypes.bool,
  openModal: PropTypes.func,
  fetchParticipantVisits: PropTypes.func,
  isUpdate: PropTypes.bool,
  participantVisit: PropTypes.shape({
    visit_metadata: PropTypes.shape({ visit_name: PropTypes.string }),
    isAdhoc: PropTypes.bool,
  }),
  pushSubsequent: PropTypes.bool,
  setDaysBeforeVisitPassed: PropTypes.func,
  setSpecificDateInvalid: PropTypes.func,
  setPushSubsequent: PropTypes.func,
  ssvpID: PropTypes.string,
  studyID: PropTypes.number,
  updateAdhocSchedule: PropTypes.func,
  updateAdhocScheduleSelection: PropTypes.func,
  updatePtpVisitMetadata: PropTypes.func,
}

export default ParticipantVisitDate
